<template>
  <div>
    <div class="test1 flex flex-wrap items-center justify-between gap-2 px-4 py-2">
      <div class="flex items-center gap-2">
        <logo
          v-if="!newsmax"
          class="h-full" />
        <p class="last-updated text-xs">Last Updated: {{ lastUpdated }}</p>
      </div>
      <topline-footer
        v-if="!uncontested"
        :data="footerData"
        :show-precincts="showPrecincts"
        :params="params"
        :last-updated="lastUpdated" />
      <div v-else>
        <p class="text-xs italic">Uncontested Race</p>
      </div>
    </div>

    <table
      :key="geoSelection"
      class="w-full">
      <thead>
        <tr>
          <th
            v-for="(header, index) in headers"
            :key="index"
            aria-label="table header"
            :class="['text-neutral pb-2 text-xs font-bold', header.classes]">
            <p
              :class="{ 'text-center': header.text === 'Pct.' || header.text === 'Party' }"
              style="color: var(--neutral-0)">
              {{ header.text }}
            </p>
          </th>
        </tr>
      </thead>
      <tbody>
        <template
          v-for="(item, index) in sortedResults"
          :key="index">
          <tr
            :class="['text-sm font-normal', item.winner && !geoSelection ? 'is-winner' : '']"
            :style="[
              getWinnerStyle(item),
              `height: ${condensed ? '52px' : '54px'};`,
              'border-bottom: 1px solid #D8D8D8;',
              `border-top: ${index == 0 ? 1 : 0}px solid #D8D8D8;`,
            ]">
            <td :style="`padding: 0; background-color: ${item.color};`" />
            <td class="pl-3">
              <div class="flex items-center gap-2 text-base">
                <span :class="{ 'text-sm': condensed }">
                  <template v-if="!['Other', 'Uncommitted', 'Yes', 'No'].includes(item.last_name)">
                    {{ item.first_name }}
                  </template>
                  {{ item.last_name }}

                  <span v-if="item.incumbent">*</span>
                </span>

                <svg
                  v-if="item.winner && !geoSelection"
                  aria-label="Winner icon"
                  class="ml-1 h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 25 25"
                  :fill="getCandColor(item)">
                  <circle
                    cx="12.5"
                    cy="12.5"
                    r="9"
                    fill="white" />
                  <path
                    v-if="data.advancing_candidates?.length > 0"
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M11.495 22.4401C17.5342 22.4401 22.43 17.5421 22.43 11.5001C22.43 5.45806 17.5342 0.560059 11.495 0.560059C5.45576 0.560059 0.559998 5.45806 0.559998 11.5001C0.559998 17.5421 5.45576 22.4401 11.495 22.4401ZM18.4271 10.853L12.0631 4.48899C11.6726 4.09847 11.0395 4.09847 10.6489 4.48899C10.2584 4.87952 10.2584 5.51268 10.6489 5.9032L15.3058 10.5601H4.56V12.5601H15.3058L10.6489 17.2169C10.2584 17.6074 10.2584 18.2406 10.6489 18.6311C11.0395 19.0217 11.6726 19.0217 12.0631 18.6311L18.4271 12.2672C18.8176 11.8766 18.8176 11.2435 18.4271 10.853Z" />
                  <path
                    v-else
                    d="M12.5 1.5625C10.3368 1.5625 8.22212 2.20397 6.42346 3.4058C4.62479 4.60763 3.22291 6.31583 2.39507 8.3144C1.56724 10.313 1.35064 12.5121 1.77267 14.6338C2.19469 16.7555 3.23639 18.7043 4.76602 20.234C6.29566 21.7636 8.24454 22.8053 10.3662 23.2273C12.4879 23.6494 14.687 23.4328 16.6856 22.6049C18.6842 21.7771 20.3924 20.3752 21.5942 18.5765C22.796 16.7779 23.4375 14.6632 23.4375 12.5C23.4375 9.59919 22.2852 6.8172 20.234 4.76602C18.1828 2.71484 15.4008 1.5625 12.5 1.5625ZM10.9375 16.8672L7.03125 12.9609L8.27344 11.7188L10.9375 14.3828L16.7266 8.59375L17.9734 9.83281L10.9375 16.8672Z" />
                </svg>
              </div>
            </td>

            <td class="text-center">
              <span
                v-if="!condensed"
                class="ml-1 px-2 py-1"
                :style="`background-color: #E6E6E6; border-radius: 3px; height: 20px; width: 40px; font-size: 12px; font-weight: 600; color: #282828`">
                {{ partyLabel(item.party_name) }}
              </span>
            </td>

            <template v-if="condensed && !uncontested">
              <td class="pl-4 pr-2 text-right">
                <div class="block font-bold">
                  {{
                    item.percentage
                      ? item.percentage === 100
                        ? 100
                        : item.percentage.toFixed(2)
                      : 0
                  }}%
                </div>
                <div class="block text-sm">
                  {{ commas(item.votes ? item.votes : 0) }}
                </div>
              </td>
            </template>
            <template v-else-if="!uncontested">
              <td class="pl-4 pr-2 text-right text-base sm:pr-8">
                {{ commas(item.votes ? item.votes : 0) }}
              </td>
              <td
                class="text-center"
                :style="{ width: '90px', backgroundColor: item.color }">
                <p
                  class="text-lg font-bold"
                  :style="{ color: readableColor(hexOrVarToHex(item.color)) }">
                  {{
                    item.percentage
                      ? item.percentage === 100
                        ? 100
                        : item.percentage.toFixed(1)
                      : 0
                  }}%
                </p>
              </td>
            </template>
          </tr>
        </template>

        <tr
          v-if="!uncontested"
          :style="[`height: 40px;`, 'border-bottom: 1px solid #D8D8D8;']">
          <td :style="`padding: 0;`" />
          <td class="pl-3">
            <div class="flex items-center gap-2">
              <span class="text-neutral gray text-xs font-bold">
                {{ $t("message.total") }}
              </span>
            </div>
          </td>
          <td class="text-center" />

          <template v-if="condensed">
            <td class="pr-3 text-right">
              <div class="text-neutral block text-xs font-bold">
                {{ commas(total) }}
              </div>
            </td>
          </template>
          <template v-else>
            <td class="gray pl-4 pr-2 text-right text-base sm:pr-8">
              {{ commas(total) }}
            </td>
          </template>
        </tr>
      </tbody>
    </table>

    <div class="mt-2 flex flex-wrap items-center justify-center">
      <div v-if="data.candidates.length > numOfCandidateByDefault">
        <button
          v-if="!showMoreCandidates"
          class="candidate-toggle"
          @click="toggleShowMoreCandidates(true)">
          {{ $t("message.showMoreCandidates") }}
        </button>
        <button
          v-else
          class="candidate-toggle"
          @click="toggleShowMoreCandidates(false)">
          {{ $t("message.showFewerCandidates") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import { capitalize, commas, partyLabel } from "@/filters";
import { getCSSVarHex, hexToRGB } from "@/helpers/utils";

import { readableColor } from "../../../helpers/readableColor";
import { ToplineFooter } from ".";
import { hexOrVarToHex } from "../../../helpers/utils";

import { Logo } from "@/components/standard/footer";

const props = {
  data: {
    type: Object,
    required: true,
  },
  condensed: {
    type: Boolean,
    required: false,
  },
  party: {
    required: true,
  },
  officeId: {
    required: true,
  },
  electionType: {
    type: String,
    required: false,
  },
  showMapLegend: {
    type: Boolean,
    required: true,
    default: true,
  },
  footerData: {
    type: Object,
    required: true,
  },
  showPrecincts: {
    type: Boolean,
    required: false,
    default: false,
  },
  params: {
    type: Object,
    required: true,
  },
  lastUpdated: {
    type: String,
    required: true,
  },
};

const computed = {
  ...mapGetters(["colors", "numOfCandidateByDefault", "theme", "showMoreCandidates"]),
  geoSelection() {
    return this.$store.getters.geoSelection;
  },
  mobilePhone() {
    return this.windowWidth <= 420;
  },
  sortedResults() {
    let data = [...this.data.candidates];
    if (!this.geoSelection) {
      data = data.sort((a, b) => b.winner - a.winner || b.votes - a.votes);
    } else {
      data = data.sort((a, b) => b.votes - a.votes);
    }
    if (!this.showMoreCandidates && data.length > this.numOfCandidateByDefault) {
      return data.slice(0, this.numOfCandidateByDefault);
    } else {
      return data;
    }
  },
  headers() {
    const votesString =
      this.data.race_id === 25407 ? `${this.$t("message.votes")}*` : this.$t("message.votes");
    let headers = [
      { text: this.$t("message.candidates"), classes: "text-left" },
      { text: this.$t("message.party"), classes: "text-right w-1/10" },
    ];

    if (!this.uncontested) {
      headers.push(
        { text: votesString, classes: "text-right pl-4 pr-2 sm:pr-8" },
        { text: this.$t("message.percentAbbreviation"), classes: "text-right w-[20px] md:w-1/10" }
      );
    }

    let condensedHeaders = [
      { text: "", classes: "py-1 w-3" },
      { text: this.$t("message.candidates"), classes: "text-left" },
    ];

    if (!this.uncontested) {
      condensedHeaders.push(
        { text: "", classes: "text-right w-1/10" },
        { text: this.$t("message.votes"), classes: "text-right w-1/10" }
      );
    }

    headers.unshift({ text: "", classes: "py-1 w-1" });

    return this.condensed ? condensedHeaders : headers;
  },
  newsmax() {
    return this.params.id === "e-cjeU" && this.params.raceIds === "25394";
  },
  total() {
    let sum = 0;

    for (const key in this.data.votes) {
      if (Object.hasOwnProperty.call(this.data.votes, key)) {
        sum += this.data.votes[key];
      }
    }

    return sum;
  },
  uncontested() {
    return this.data.uncontested;
  },
};

const methods = {
  commas,
  partyLabel,
  hexOrVarToHex,
  readableColor,
  ...mapActions({
    toggleShowMoreCandidates: "toggleShowMoreCandidates",
  }),
  getWinnerStyle(item) {
    if (this.geoSelection || !item.winner) return;
    let hex = null;
    if (["Republican", "Democratic", "Green", "Libertarian"].includes(item.party_name)) {
      const partyName = capitalize(item.party_name);
      hex = getCSSVarHex(this.theme, this.colors.party[partyName]);
    } else if (["Yes", "No"].includes(item.last_name)) {
      hex = getCSSVarHex(this.theme, item.color);
    } else {
      hex = getCSSVarHex(this.theme, this.colors.party.Other);
    }
    return `background-color: ${hexToRGB(hex, 0.2)} !important;`;
  },
  getCandColor(cand) {
    if (
      [
        "Republican",
        "Democratic",
        "Green",
        "Libertarian",
        "Progressive",
        "Independent",
        "United Utah",
      ].includes(cand.party_name)
    ) {
      const partyName = capitalize(cand.party_name);
      return this.colors.party[partyName];
    } else if (["Yes", "No"].includes(cand.last_name)) {
      return cand.color;
    } else {
      return this.colors.party.Other;
    }
  },
  getWinnerTextStyle(item) {
    let styles = "";
    if (!this.geoSelection && item.winner) {
      styles = styles + "color: #222; font-weight: 700;";
    }
    return styles;
  },
  showParty(item) {
    // TODO: break this out to make it more readable and easier to add exceptions - potentially move it to parser
    // also do the same on lightweight embed

    // if office type is supreme court
    // if (this.officeId === 30) {
    //   return false
    // }
    // if (this.allSameParty()) return false
    const nonCandidates = ["Yes", "No", "Approve", "Reject"];

    if (item.party_name === "" || this.party) return false;
    if (nonCandidates.includes(item.last_name) || nonCandidates.includes(item.first_name))
      return false;
    return true;
    // const case1 = (!['primary', 'presidential primary'].includes(this.electionType) && ![25, 99].includes(this.officeId) && item.cand_id !== 1802)
    // const case2 = (this.electionType === 'primary' && !this.party)
    // return case1 || case2
  },
  // TODO: need to move this business logic somewhere else
  allSameParty() {
    return [...new Set(this.sortedResults.map((item) => item.party_name))].length === 1;
  },
};

export default {
  components: { ToplineFooter, Logo },
  props,
  data() {
    return {
      windowWidth: null,
    };
  },
  computed,
  mounted() {
    this.windowWidth = window.innerWidth;
    window.addEventListener("resize", () => {
      this.windowWidth = window.innerWidth;
    });
  },
  beforeUnmount() {
    window.removeEventListener("resize", () => {
      this.windowWidth = window.innerWidth;
    });
  },
  methods,
};
</script>

<style>
.is-winner td {
  font-weight: bold;
}

.candidate-toggle {
  font-size: 14px;
  font-weight: 600;
  color: var(--neutral-0);
}

.candidate-toggle:hover,
.candidate-toggle:focus {
  color: var(--neutral-3);
}

.test {
  display: flex;
}

.test1 {
  background-color: var(--neutral-1);
  margin: 18px 0;
}

.last-updated {
  color: var(--text-neutral);
}

.gray {
  color: var(--neutral-0);
}
</style>
